import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { handleGetProfileData, setExceptionHandler, getModalSwitch } from '@app/store/actions';
import PageLoader from '@app/components/PageLoader/pageLoader.component';
import PersonIcon from '@material-ui/icons/Person';
import EmailIcon from '@material-ui/icons/Email';
import { Link } from '@material-ui/core';
import { basicService } from '@app/services';
import { DATE_FORMAT, passlyFormatUnixMilliseconds } from '@app/helpers';
import { numberOfDevices } from '@app/components/Common/trustedDevices.shared';
import { TrustedDevices, ModalComponent, IModalData, AvatarComponent } from '@app/components';
import ChangePhoneDialog from '@app/pages/profile/dialogs/changePhone.dialog';
import ChangePasswordDialog from '@app/pages/profile/dialogs/changePassword.dialog';
import { MfaChangeButtons } from './components';

const MyProfilePage = () => {
    const myProfile = useSelector((state: any) => state.myProfile.myProfile);
    const myProfileLoading = useSelector((state: any) => state.myProfile.profileLoading);
    const myProfileGrants = useSelector((state: any) => state.myProfile.myProfileGrants);
    const currentUserId = useSelector((state: any) => state.signIn.currentUser.userUuid as string);

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    useEffect(() => {
        dispatch(handleGetProfileData());
        dispatch(setExceptionHandler(true));
    }, []);

    const changePhone = (changing: boolean) => {
        dispatch(getModalSwitch(true));

        let header: string = '';

        if (changing) {
            header = translation('CHANGE_TWO_STEP_PHONE_LINK');
        } else {
            header = translation('SETUP_TWO_STEP_PHONE_LINK');
        }

        setModalData({
            headerTitle: header,
            modalSwitcher: modalSwitchState,
            form: <ChangePhoneDialog handleNewPhoneNumber={handleNewPhoneNumber} />
        });
    };

    const handleNewPhoneNumber = (phoneNumber: string) => {
        myProfile.signingIn.registeredPhone = phoneNumber;
    };

    const changePassword = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('CHANGE_PASSWORD'),
            modalSwitcher: modalSwitchState,
            form: <ChangePasswordDialog passwordPolicy={myProfile.person.passwordPolicy} handleNewPassword={handlePasswordChanged} />
        });
    };

    const handlePasswordChanged = () => {
        myProfile.person.attributes.passwordLastChanged = new Date();
    };

    return (
        <>
            <ModalComponent {...modalData} />

            {myProfileLoading === false && myProfile !== undefined && myProfileGrants !== undefined ? (
                <div className='content--narrow detailPage'>
                    <div className='detailPage__header'>
                        <div className='detailPage__header-cover'>
                            <div className='detailPage__header-detail'>
                                <div className='detailPage__header-detail-content'>
                                    <AvatarComponent userId={myProfile.id} size='large' />
                                </div>
                            </div>
                            <div className='detailPage__profile-large-title'>
                                <h1 className='detailPage__display-name'>{myProfile.displayName}</h1>
                                <h3 className='detailPage__principal-name'>
                                    <span className='detailPage__principal-name-icon d__inline__block v_align__m'>
                                        <PersonIcon />
                                    </span>
                                    <span className='detailPage__principal-name-icon d__inline__block v_align__m'>{myProfile.principalName}</span>
                                </h3>
                                <h5 className='detailPage__principal-name-h5'>
                                    <span className='detailPage__principal-name-icon d__inline__block v_align__m'>
                                        <EmailIcon />
                                    </span>
                                    <span>{myProfile.emailAddress}</span>
                                </h5>
                            </div>
                        </div>
                        <div className='detailPage__signed-in-as detailPage__apn'>
                            <div className='detailPage__location-info'>
                                {translation('SIGNED_IN_AS_LABEL')}&nbsp;
                                <strong>{myProfile.currentPrincipalName}</strong>
                            </div>
                            {myProfile.ipInfo && (
                                <div className='detailPage__location-info'>
                                    <strong>
                                        {myProfile.ipInfo.city}, {myProfile.ipInfo.state}, {myProfile.ipInfo.country}
                                    </strong>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className='detailPage__header-specifics'>
                        <div className='detailPage__specifics-container'>
                            <div className='detailPage__specific'>
                                <div className='detailPage__specific-detail'>
                                    {myProfile.person.attributes.lastLogin ? (
                                        <span>{passlyFormatUnixMilliseconds(myProfile.person.attributes.lastLogin, DATE_FORMAT)}</span>
                                    ) : (
                                        <span>{translation('NEVER_LOGGED_ON')}</span>
                                    )}
                                </div>
                                <span className='detailPage__specific-detail-label'>{translation('USERS_LAST_LOGON')}</span>
                            </div>
                            <div className='detailPage__specific'>
                                <div className='detailPage__specific-detail'>{numberOfDevices(myProfileGrants.Grants)}</div>
                                <span className='detailPage__specific-detail-label'>{translation('TRUSTED_DEVICES_SECTION')}</span>
                            </div>
                        </div>
                    </div>

                    <div className='detailPage__section'>
                        <div className='detailPage__section-head'>{translation('SIGNING_IN_HEADER')}</div>
                        <div className='detailPage__section-content'>
                            <ul className='detailPage__list'>
                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('EMAIL_ADDR')}</span>
                                    <span className='detailPage__list--secondary-label'>{myProfile.emailAddress}</span>
                                </li>
                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('USERNAME')}</span>
                                    <span className='detailPage__list--secondary-label'>
                                        {myProfile.principalName}
                                        {myProfile.alternatePrincipalNames &&
                                            myProfile.alternatePrincipalNames.length > 0 &&
                                            myProfile.alternatePrincipalNames.map((item: string, index: number) => (
                                                <span className='detailPage__list--secondary-label-child' key={index}>
                                                    {item}
                                                </span>
                                            ))}
                                    </span>
                                </li>

                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('PASSWORD_PROFILE_BLOCK_TITLE')}</span>
                                    {myProfile.person.canChangePassword ? (
                                        <span className='detailPage__list--secondary-label'>
                                            {!myProfile.person.attributes.passwordLastChanged ? (
                                                <span>{translation('PASSWORD_NEVER_CHANGED')}</span>
                                            ) : (
                                                <span>
                                                    <span>{translation('PASSWORD_LAST_CHANGED')}</span>
                                                    <span className='detailPage__list--secondary-label-timestamp'>
                                                        {passlyFormatUnixMilliseconds(myProfile.person.attributes.passwordLastChanged, DATE_FORMAT)}
                                                    </span>
                                                </span>
                                            )}
                                            <div>
                                                <Link id='changePasswordLink' onClick={() => changePassword()}>
                                                    {translation('CHANGE_PASSWORD')}
                                                </Link>
                                            </div>
                                        </span>
                                    ) : (
                                        <span>{translation('PASSWORD_MANAGED_BY_SYNC')}</span>
                                    )}
                                </li>

                                <li>
                                    <span className='detailPage__list--primary-label'>{translation('TWO_STEP_VERIFICATION_PROFILE_BLOCK_TITLE')}</span>
                                    {!myProfile.signingIn.registeredPhone ? (
                                        <span className='detailPage__list--secondary-label'>
                                            <span>{translation('TWO_STEP_REGISTERED_NUMBER_NOT_SET')}</span>
                                            <div>
                                                <Link id='setupPhoneLink' onClick={() => changePhone(false)}>
                                                    {translation('SETUP_TWO_STEP_PHONE_LINK')}
                                                </Link>
                                            </div>
                                        </span>
                                    ) : (
                                        <span className='detailPage__list--secondary-label'>
                                            <span>{translation('TWO_STEP_REGISTERED_NUMBER')}</span>
                                            <span className='detailPage__list--secondary-label-timestamp'>{myProfile.signingIn.registeredPhone}</span>
                                            <div>
                                                <Link id='updatePhoneLink' onClick={() => changePhone(true)}>
                                                    {translation('CHANGE_TWO_STEP_PHONE_LINK')}
                                                </Link>
                                            </div>
                                        </span>
                                    )}
                                </li>

                                <li>
                                    <span className='detailPage__list--primary-label'> {translation('MULTIFACTOR_AUTH_PROFILE_BLOCK_TITLE')} </span>
                                    <span className='detailPage__list--secondary-label'>
                                        {!myProfile.person.attributes.mfaKeyPresent ? (
                                            <span className='detailPage__list--row'>
                                                <span>{translation('MULTIFACTOR_AUTH_NEVER_USED')}</span>
                                            </span>
                                        ) : (
                                            <span className='detailPage__list--row'>
                                                <span>{translation('MULTIFACTOR_AUTH_LAST_USED_DATE')}</span>
                                                <span className='detailPage__list--secondary-label-timestamp'>{passlyFormatUnixMilliseconds(myProfile.signingIn.mfaLastUsed, DATE_FORMAT)}</span>
                                            </span>
                                        )}

                                        <MfaChangeButtons myProfile={myProfile} modalSwitchState={modalSwitchState} />
                                        {myProfile.person.attributes.mfaLastUsed !== 0 && (
                                            <span className='detailPage__list--row'>
                                                <Link id='testTokenLink' onClick={() => basicService.redirect('/profile/testmfa')}>
                                                    {translation('TEST_TOKEN')}
                                                </Link>
                                            </span>
                                        )}
                                    </span>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <TrustedDevices grantsInput={myProfileGrants.Grants} isMyProfile={true} userId={currentUserId} />
                </div>
            ) : (
                <PageLoader />
            )}
        </>
    );
};

export default MyProfilePage;
